export const intro = [
  {
    id: "7001",
    code: "",
    content:
      "## Introduction to C++\n\n#### Why Learn C++?\nC++ is a general-purpose programming language that offers a blend of high- and low-level language features. Whether you're developing system software, game engines, or complex algorithms, C++ offers performance and flexibility.\n\n#### A Brief History\nC++ was developed as an extension to the C programming language, adding features like classes and objects to support the object-oriented paradigm.\n\n#### C++ in the Industry\nC++ is widely used in various sectors like finance, game development, embedded systems, and more. Its performance-critical nature makes it a go-to choice for many organizations.",
    kind: "info screen",
    slug: "introduction",
  },
  {
    id: "7002",
    code:
      '```cpp\n#include <iostream>\n\nint main() {\n  std::cout << "Hello, World!" << std::endl;\n  return 0;\n}\n```',
    content:
      "#### Your First C++ Program\nIn this snippet, we see a simple C++ program that outputs 'Hello, World!' to the console. Let's break down its components:\n\n- `#include <iostream>`: Includes the input-output stream library.\n- `int main()`: The main function where execution starts.\n- `std::cout`: The C++ output stream.\n- `std::endl`: Inserts a new line and flushes the stream.\n- `return 0;`: Exits the program.",
    kind: "info screen",
    slug: "ex1",
  },
  {
    id: "7003",
    code: "",
    content:
      "## Setting up the C++ Development Environment\n\n#### Choosing an IDE\nYou can choose from a range of Integrated Development Environments (IDEs) like Visual Studio, Code::Blocks, or even simple text editors coupled with a compiler.\n\n#### Installing a Compiler\nThe GNU Compiler Collection (GCC) is a popular choice for compiling C++ code. You can install it through package managers on Linux and macOS, or download it for Windows.\n\n#### Verifying the Setup\nTo verify your setup, compile and run a simple 'Hello, World!' program. If you see the output, you're good to go!",
    kind: "info screen",
    slug: "ex2",
  },
  {
    id: "7004",
    code:
      "```bash\n# Compiling a C++ program\n$ g++ hello.cpp -o hello\n\n# Running the compiled program\n$ ./hello\n```",
    content:
      "#### Compiling and Running C++ Code\nOnce you've written your C++ code in a file (typically with a `.cpp` extension), you'll need to compile it into an executable. Here's how you can do that:\n\n- `g++ hello.cpp -o hello`: This command compiles the `hello.cpp` file and outputs an executable named `hello`.\n- `./hello`: This command runs the compiled executable, displaying any output to the terminal.",
    kind: "info screen",
    slug: "ex3",
  },
];

export const loops = [
  {
    id: "101",
    code:
      '```cpp\n#include <iostream>\nint main() {\n  if (true) {\n    std::cout << "This is true!";\n  }\n  return 0;\n}\n```',
    content:
      "## Introduction to Conditionals\n\n#### What are Conditionals?\nConditionals allow you to make decisions in your code. The most basic type is the `if` statement.\n\n```cpp\nif (true) {\n  // code to execute if condition is true\n}\n```\n\nUnderstanding `if` statements is your first step in mastering control structures.",
    kind: "info screen",
    title: "Intro to Conditionals",
    slug: "intro-to-conditionals",
  },
  {
    id: "102",
    code:
      '```cpp\n#include <iostream>\nint main() {\n  int num = 5;\n  if (num > 0) {\n    std::cout << "Positive";\n  } else {\n    std::cout << "Non-positive";\n  }\n  return 0;\n}\n```',
    content:
      "## If-Else Statements\n\n#### Expanding the `if` Statement\nYou can add an `else` block to execute code when the condition is false.\n\n```cpp\nif (condition) {\n  // true block\n} else {\n  // false block\n}\n```\n\nThis structure lets you handle both true and false outcomes.",
    kind: "info screen",
    title: "If-Else",
    slug: "if-else",
  },
  {
    id: "103",
    code:
      '```cpp\n#include <iostream>\nint main() {\n  int num = 0;\n  if (num > 0) {\n    std::cout << "Positive";\n  } else if (num < 0) {\n    std::cout << "Negative";\n  } else {\n    std::cout << "Zero";\n  }\n  return 0;\n}\n```',
    content:
      "## If-ElseIf-Else Chains\n\n#### Multiple Conditions\nFor more than two conditions, use `if`, `else if`, and `else`.\n\n```cpp\nif (condition1) {\n  // block1\n} else if (condition2) {\n  // block2\n} else {\n  // block3\n}\n```\n\nThis allows you to handle multiple scenarios.",
    kind: "info screen",
    title: "If-ElseIf-Else",
    slug: "if-elseif-else",
  },
  {
    id: "104",
    code:
      "```cpp\n#include <iostream>\nint main() {\n  for (int i = 0; i < 5; ++i) {\n    std::cout << i << ' ';\n  }\n  return 0;\n}\n```",
    content:
      "## Introduction to Loops\n\n#### The `for` Loop\nA `for` loop allows you to repeat code.\n\n```cpp\nfor (init; condition; update) {\n  // loop body\n}\n```\n\nThe `for` loop is handy for situations where the number of iterations is known.",
    kind: "info screen",
    title: "For Loop",
    slug: "for-loop",
  },
  {
    id: "105",
    code:
      "```cpp\n#include <iostream>\nint main() {\n  int i = 0;\n  while (i < 5) {\n    std::cout << i << ' ';\n    ++i;\n  }\n  return 0;\n}\n```",
    content:
      "## While Loops\n\n#### The `while` Loop\nUse a `while` loop when the number of iterations is unknown.\n\n```cpp\nwhile (condition) {\n  // loop body\n}\n```\n\nThe `while` loop checks the condition before each iteration.",
    kind: "info screen",
    title: "While Loop",
    slug: "while-loop",
  },
  {
    id: "106",
    code:
      "```cpp\n#include <iostream>\nint main() {\n  int i = 0;\n  do {\n    std::cout << i << ' ';\n    ++i;\n  } while (i < 5);\n  return 0;\n}\n```",
    content:
      "## Do-While Loops\n\n#### Post-Check Loop\nThe `do-while` loop checks the condition after each iteration.\n\n```cpp\ndo {\n  // loop body\n} while (condition);\n```\n\nUse `do-while` when the loop body must execute at least once.",
    kind: "info screen",
    title: "Do-While Loop",
    slug: "do-while-loop",
  },
  {
    id: "107",
    code:
      "```cpp\n#include <iostream>\nint main() {\n  for (int i = 0; i < 5; ++i) {\n    if (i == 2) {\n      break;\n    }\n    std::cout << i << ' ';\n  }\n  return 0;\n}\n```",
    content:
      "## Using `break`\n\n#### Exiting Loops\nThe `break` statement allows you to exit a loop early.\n\n```cpp\nif (condition) {\n  break;\n}\n```\n\nUse `break` to stop a loop based on some condition.",
    kind: "info screen",
    title: "Break",
    slug: "break",
  },
  {
    id: "108",
    code:
      "```cpp\n#include <iostream>\nint main() {\n  for (int i = 0; i < 5; ++i) {\n    if (i == 2) {\n      continue;\n    }\n    std::cout << i << ' ';\n  }\n  return 0;\n}\n```",
    content:
      "## Using `continue`\n\n#### Skipping Iterations\nThe `continue` statement skips the current iteration.\n\n```cpp\nif (condition) {\n  continue;\n}\n```\n\nUse `continue` to avoid executing certain iterations.",
    kind: "info screen",
    title: "Continue",
    slug: "continue",
  },
  {
    id: "109",
    code:
      '```cpp\n#include <iostream>\nint main() {\n  int choice;\n  std::cin >> choice;\n  switch (choice) {\n    case 1: std::cout << "One"; break;\n    case 2: std::cout << "Two"; break;\n    default: std::cout << "Other"; break;\n  }\n  return 0;\n}\n```',
    content:
      "## Switch-Case\n\n#### Multiple Choices\nA `switch-case` statement lets you choose among multiple cases.\n\n```cpp\nswitch (variable) {\n  case value1: // code block1\n  break;\n  case value2: // code block2\n  break;\n  default: // default block\n}\n```\n\nSwitch-case structures are especially useful for menu-driven programs.",
    kind: "info screen",
    title: "Switch-Case",
    slug: "switch-case",
  },
  {
    id: "110",
    code: "",
    content:
      "- **Conditionals**: Use `if`, `if-else`, and `if-elseif-else` to make decisions.\n- **Loops**: Master the `for`, `while`, and `do-while` loops for repetition.\n- **Control Statements**: Understand the use of `break` and `continue`.\n- **Switch-Case**: Suitable for menu-driven programs.\n\nBy mastering these control structures, you'll be well-prepared for coding interviews.",
    kind: "info screen",
    title: "Summary",
    slug: "summary",
  },
];

export const ml = [
  {
    id: "201",
    code: "```javascript\nconst data = [1, 2, 3, 4, 5];\n```",
    content:
      "## Introduction to ML\n\n#### What is Machine Learning?\nMachine learning is a subset of artificial intelligence that enables computers to learn from data.\n\n```javascript\nconst data = [1, 2, 3, 4, 5];\n```\n\nTo get started, you'll often use data arrays like the one above.",
    kind: "info screen",
    title: "Introduction to ML",
    slug: "introduction-to-ml",
  },
  {
    id: "202",
    code:
      "```javascript\nconst labels = ['apple', 'orange', 'apple', 'orange'];\n```",
    content:
      "## Labels and Features\n\n#### Data Components\nMachine learning models need both features and labels to train on.\n\n```javascript\nconst labels = ['apple', 'orange', 'apple', 'orange'];\n```\n\nLabels categorize the data you have.",
    kind: "info screen",
    title: "Labels and Features",
    slug: "labels-and-features",
  },
  {
    id: "203",
    code: "```javascript\nimport * as tf from '@tensorflow/tfjs';\n```",
    content:
      "## TensorFlow.js\n\n#### Javascript ML Library\nTensorFlow.js is a library for machine learning in Javascript.\n\n```javascript\nimport * as tf from '@tensorflow/tfjs';\n```\n\nThis library allows you to define, train, and deploy models right in the browser.",
    kind: "info screen",
    title: "TensorFlow.js",
    slug: "tensorflow-js",
  },
  {
    id: "204",
    code: "```javascript\nconst model = tf.sequential();\n```",
    content:
      "## Creating a Model\n\n#### Sequential Models\nThe most basic type of model is a sequential model.\n\n```javascript\nconst model = tf.sequential();\n```\n\nThis is good for a plain stack of layers where each layer has exactly one input tensor and one output tensor.",
    kind: "info screen",
    title: "Creating a Model",
    slug: "creating-a-model",
  },
  {
    id: "205",
    code:
      "```javascript\nmodel.add(tf.layers.dense({units: 1, inputShape: [1]}));\n```",
    content:
      "## Adding Layers\n\n#### Dense Layer\nIn TensorFlow.js, you can add a dense layer to a model like so.\n\n```javascript\nmodel.add(tf.layers.dense({units: 1, inputShape: [1]}));\n```\n\nDense layers are fully connected layers.",
    kind: "info screen",
    title: "Adding Layers",
    slug: "adding-layers",
  },
  {
    id: "206",
    code:
      "```javascript\nmodel.compile({loss: 'meanSquaredError', optimizer: 'sgd'});\n```",
    content:
      "## Compiling a Model\n\n#### Loss and Optimizer\nBefore training, you need to compile the model.\n\n```javascript\nmodel.compile({loss: 'meanSquaredError', optimizer: 'sgd'});\n```\nThe `loss` and `optimizer` are two essential arguments.",
    kind: "info screen",
    title: "Compiling a Model",
    slug: "compiling-a-model",
  },
  {
    id: "207",
    code:
      "```javascript\nconst xs = tf.tensor2d([1, 2, 3, 4], [4, 1]);\nconst ys = tf.tensor2d([1, 3, 5, 7], [4, 1]);\n```",
    content:
      "## Preparing Data\n\n#### Tensors\nData needs to be converted to tensors before training.\n\n```javascript\nconst xs = tf.tensor2d([1, 2, 3, 4], [4, 1]);\nconst ys = tf.tensor2d([1, 3, 5, 7], [4, 1]);\n```\n\nTensors are a generalization of arrays.",
    kind: "info screen",
    title: "Preparing Data",
    slug: "preparing-data",
  },
  {
    id: "208",
    code: "```javascript\nmodel.fit(xs, ys, {epochs: 10});\n```",
    content:
      "## Training the Model\n\n#### Fitting Data\nUse `fit` to train the model.\n\n```javascript\nmodel.fit(xs, ys, {epochs: 10});\n```\n\nThe `epochs` parameter specifies the number of times the learning algorithm will work through the entire training dataset.",
    kind: "info screen",
    title: "Training the Model",
    slug: "training-the-model",
  },
  {
    id: "209",
    code:
      "```javascript\nconst output = model.predict(tf.tensor2d([5], [1, 1]));\n```",
    content:
      "## Making Predictions\n\n#### The `predict` Method\nOnce trained, use your model to make predictions.\n\n```javascript\nconst output = model.predict(tf.tensor2d([5], [1, 1]));\n```\n\nThe `predict` method outputs predictions based on the input.",
    kind: "info screen",
    title: "Making Predictions",
    slug: "making-predictions",
  },
  {
    id: "210",
    code: "",
    content:
      "- **Introduction**: Understand what machine learning is.\n- **Data Components**: Learn about features and labels.\n- **TensorFlow.js**: Utilize this library for ML in Javascript.\n- **Creating Models**: Know how to create and compile models.\n- **Data**: Turn your data into tensors.\n- **Training**: Use the `fit` method to train models.\n- **Prediction**: Make predictions using trained models.\n\nBy mastering these basics, you'll lay the foundation for more advanced ML topics.",
    kind: "info screen",
    title: "Summary",
    slug: "summary",
  },
];

export const transferLearning = [
  {
    id: "201",
    code: "```javascript\n// No code for this screen\n```",
    content:
      "## Introduction\n\n#### What is Transfer Learning?\nTransfer Learning is a machine learning technique where you apply the knowledge gained from one task to another similar task.\n\n- **Pre-trained Models**: Utilize models trained on one task for another task.\n\nUnderstanding transfer learning sets the stage for more advanced machine learning techniques.",
    kind: "info screen",
    title: "Introduction",
    slug: "introduction",
  },
  {
    id: "202",
    code:
      "```javascript\n// Import TensorFlow.js\ncosnt tf = require('@tensorflow/tfjs');\n```",
    content:
      "## Setup\n\n#### Getting Started\nTo begin with transfer learning, you'll need TensorFlow.js.\n\n```javascript\nconst tf = require('@tensorflow/tfjs');\n```\n\nEnsure that you've installed TensorFlow.js as it's essential for machine learning in JavaScript.",
    kind: "info screen",
    title: "Setup",
    slug: "setup",
  },
  {
    id: "203",
    code:
      "```javascript\n// Load pre-trained model\nconst model = await tf.loadLayersModel('path/to/model');\n```",
    content:
      "## Loading Models\n\n#### How to Load a Pre-trained Model\nYou can easily load a pre-trained model using TensorFlow.js.\n\n```javascript\nconst model = await tf.loadLayersModel('path/to/model');\n```\n\nLoading a pre-trained model is the first step in transfer learning.",
    kind: "info screen",
    title: "Loading Models",
    slug: "loading-models",
  },
  {
    id: "204",
    code: "```javascript\n// Remove last layer\nmodel.layers.pop();\n```",
    content:
      "## Modifying Models\n\n#### Tailoring for Your Task\nOften, you'll need to modify the pre-trained model to suit your specific task.\n\n```javascript\nmodel.layers.pop();\n```\n\nRemoving layers or adding new ones can tailor the model to your needs.",
    kind: "info screen",
    title: "Modifying Models",
    slug: "modifying-models",
  },
  {
    id: "205",
    code:
      "```javascript\n// Add new layer\nmodel.add(tf.layers.dense({units: 10, activation: 'softmax'}));\n```",
    content:
      "## Adding Layers\n\n#### Customization\nAfter removing layers, you can add new layers that are more relevant to your task.\n\n```javascript\nmodel.add(tf.layers.dense({units: 10, activation: 'softmax'}));\n```\n\nAdding layers helps in customizing the pre-trained model.",
    kind: "info screen",
    title: "Adding Layers",
    slug: "adding-layers",
  },
  {
    id: "206",
    code:
      "```javascript\n// Compile the model\nmodel.compile({optimizer: 'sgd', loss: 'categoricalCrossentropy'});\n```",
    content:
      "## Compilation\n\n#### Prepare for Training\nBefore you can train your modified model, you need to compile it.\n\n```javascript\nmodel.compile({optimizer: 'sgd', loss: 'categoricalCrossentropy'});\n```\n\nCompilation is crucial as it prepares your model for training.",
    kind: "info screen",
    title: "Compilation",
    slug: "compilation",
  },
  {
    id: "207",
    code:
      "```javascript\n// Train the model\nconst history = await model.fit(xs, ys);\n```",
    content:
      "## Training\n\n#### Fine-Tuning\nOnce compiled, you can train your model on the new data.\n\n```javascript\nconst history = await model.fit(xs, ys);\n```\n\nTraining (or fine-tuning) adapts the model to your specific task.",
    kind: "info screen",
    title: "Training",
    slug: "training",
  },
  {
    id: "208",
    code:
      "```javascript\n// Evaluate the model\nconst score = model.evaluate(xTest, yTest);\n```",
    content:
      "## Evaluation\n\n#### Check Performance\nAfter training, it's essential to evaluate your model.\n\n```javascript\nconst score = model.evaluate(xTest, yTest);\n```\n\nEvaluation gives you a metric to understand the model's performance.",
    kind: "info screen",
    title: "Evaluation",
    slug: "evaluation",
  },
  {
    id: "209",
    code:
      "```javascript\n// Save the model\nawait model.save('path/to/save');\n```",
    content:
      "## Saving the Model\n\n#### Preservation for Future Use\nAfter evaluation, you can save your fine-tuned model.\n\n```javascript\nawait model.save('path/to/save');\n```\n\nSaving allows you to reuse or share the model.",
    kind: "info screen",
    title: "Saving the Model",
    slug: "saving-the-model",
  },
  {
    id: "210",
    code: "",
    content:
      "- **Introduction**: Transfer learning enables leveraging pre-trained models.\n- **Setup**: TensorFlow.js is crucial for ML in JavaScript.\n- **Loading and Modifying**: Import a model and adapt it for your needs.\n- **Training and Evaluation**: Fine-tune and assess the model.\n- **Saving**: Preserve your work for future use.\n\nBy grasping these concepts, you'll be well-equipped for more advanced machine learning tasks.",
    kind: "info screen",
    title: "Summary",
    slug: "summary",
  },
];

export const matrix = [
  {
    id: "201",
    code:
      "```javascript\nconst matrixA = [[1, 2], [3, 4]];\nconst matrixB = [[5, 6], [7, 8]];\n```",
    content:
      "## Introduction to Matrices\n\n#### What is a Matrix?\nA matrix is a two-dimensional array of numbers arranged in rows and columns. They are foundational in machine learning.\n\n```javascript\nconst matrixA = [[1, 2], [3, 4]];\nconst matrixB = [[5, 6], [7, 8]];\n```\n\nUnderstanding matrices is crucial for diving into machine learning algorithms.",
    kind: "info screen",
    title: "Intro to Matrices",
    slug: "intro-to-matrices",
  },
  {
    id: "202",
    code:
      "```javascript\nconst matrixA = [[1, 2], [3, 4]];\nconst rowsA = matrixA.length;\nconst colsA = matrixA[0].length;\nconsole.log(`Rows: ${rowsA}, Cols: ${colsA}`);\n```",
    content:
      "## Dimensions of a Matrix\n\n#### Rows and Columns\nEvery matrix has a specific number of rows and columns. Knowing these dimensions is essential for operations like multiplication.\n\n```javascript\nconst rowsA = matrixA.length;\nconst colsA = matrixA[0].length;\n```\n\nYou'll often hear dimensions referred to as 'm x n', where 'm' is the number of rows and 'n' is the number of columns.",
    kind: "info screen",
    title: "Matrix Dimensions",
    slug: "matrix-dimensions",
  },
  {
    id: "203",
    code:
      "```javascript\nconst matrixA = [[1, 2], [3, 4]];\nconst matrixB = [[5, 6], [7, 8]];\nconst isValid = (matrixA[0].length === matrixB.length);\nconsole.log(`Can multiply: ${isValid}`);\n```",
    content:
      "## Pre-requisites for Matrix Multiplication\n\n#### Compatibility Check\nFor matrix multiplication to be valid, the number of columns in the first matrix must equal the number of rows in the second matrix.\n\n```javascript\nconst isValid = (matrixA[0].length === matrixB.length);\n```\n\nIf this condition is not met, multiplication isn't possible.",
    kind: "info screen",
    title: "Multiplication Pre-requisites",
    slug: "multiplication-prerequisites",
  },
  {
    id: "204",
    code:
      "```javascript\nconst matrixA = [[1, 2], [3, 4]];\nconst matrixB = [[5, 6], [7, 8]];\nconst result = [];\nfor (let i = 0; i < matrixA.length; i++) {\n  const row = [];\n  for (let j = 0; j < matrixB[0].length; j++) {\n    let sum = 0;\n    for (let k = 0; k < matrixA[0].length; k++) {\n      sum += matrixA[i][k] * matrixB[k][j];\n    }\n    row.push(sum);\n  }\n  result.push(row);\n}\nconsole.log(`Result: ${JSON.stringify(result)}`);\n```",
    content:
      "## The Multiplication Algorithm\n\n#### Step-by-Step Guide\nMatrix multiplication involves three nested loops. The outer two loops iterate over each cell in the resulting matrix, and the innermost loop performs the sum of products.\n\n```javascript\n// Initialize result matrix\n// Three nested loops\n// Compute sum of products\n```\n\nUnderstanding this algorithm is essential for implementing machine learning models.",
    kind: "info screen",
    title: "Multiplication Algorithm",
    slug: "multiplication-algorithm",
  },
  {
    id: "205",
    code: "",
    content:
      "## Matrix Multiplication in ML\n\n#### Why It Matters\nMatrix multiplication is central in machine learning algorithms. It's used in linear regression, neural networks, and many other algorithms.\n\n- **Speed**: Efficient matrix multiplication is crucial for fast computation.\n- **Data Transformation**: Matrices can represent feature sets, and multiplication helps in feature transformation.\n\nBy understanding matrix multiplication, you're laying a strong foundation for diving into machine learning.",
    kind: "info screen",
    title: "Matrix Multiplication in ML",
    slug: "matrix-multiplication-in-ml",
  },
  {
    id: "206",
    code: "",
    content:
      "- **Matrices**: Understand the basics and dimensions.\n- **Pre-requisites**: Ensure the matrices are compatible for multiplication.\n- **Algorithm**: Master the three nested loops for multiplication.\n- **ML Context**: Recognize the importance of matrices in machine learning algorithms.\n\nBy mastering matrix multiplication, you're taking a significant step in preparing for machine learning challenges and coding interviews.",
    kind: "info screen",
    title: "Summary",
    slug: "summary",
  },
];

export const recursionInGo = [
  {
    code:
      '```go\npackage main\nimport "fmt"\nfunc main() {\n  fmt.Println("Hello, Recursion!")\n}\n```',
    content:
      "## Introduction\n\n#### What is Recursion?\nRecursion is a programming technique where a function calls itself to solve a problem. It's a powerful concept often used in algorithms and problem-solving.\n\n- **Base Case**: The condition under which the recursion stops.\n- **Recursive Case**: The condition under which the function calls itself.\n\nUnderstanding these concepts is crucial for mastering recursion.",
    kind: "info screen",
    title: "Recursion Basics",
  },
  {
    code:
      '```go\npackage main\nimport "fmt"\nfunc factorial(n int) int {\n  if n == 1 {\n    return 1\n  }\n  return n * factorial(n-1)\n}\nfunc main() {\n  fmt.Println(factorial(5))\n}\n```',
    content:
      "## Factorial\n\n#### The Classic Example\nThe factorial of a number is often calculated using recursion. The factorial of 5 is (5 \times 4 \times 3 \times 2 \times 1 = 120).\n\n- **Base Case**: (n = 1)\n- **Recursive Case**: (n \times \text{factorial}(n-1))\n\nThis is one of the most straightforward examples of recursion.",
    kind: "info screen",
    title: "Factorial Example",
  },
  {
    code:
      '```go\npackage main\nimport "fmt"\nfunc fibonacci(n int) int {\n  if n <= 1 {\n    return n\n  }\n  return fibonacci(n-1) + fibonacci(n-2)\n}\nfunc main() {\n  fmt.Println(fibonacci(5))\n}\n```',
    content:
      "## Fibonacci Series\n\n#### Another Classic Example\nThe Fibonacci sequence is another typical example where recursion can be applied. The sequence starts as 0, 1, 1, 2, 3, 5, 8, ...\n\n- **Base Cases**: (n = 0) or (n = 1)\n- **Recursive Case**: (\text{fibonacci}(n-1) + \text{fibonacci}(n-2))\n\nHere, we've used recursion to calculate the fifth Fibonacci number.",
    kind: "info screen",
    title: "Fibonacci Example",
  },
  {
    code:
      '```go\npackage main\nimport "fmt"\nfunc sumOfDigits(n int) int {\n  if n == 0 {\n    return 0\n  }\n  return n % 10 + sumOfDigits(n / 10)\n}\nfunc main() {\n  fmt.Println(sumOfDigits(123))\n}\n```',
    content:
      "## Sum of Digits\n\n#### Breaking Down Numbers\nYou can find the sum of the digits of a number using recursion. For example, the sum of digits of 123 is (1 + 2 + 3 = 6).\n\n- **Base Case**: (n = 0)\n- **Recursive Case**: (n % 10 + \text{sumOfDigits}(n / 10))\n\nThis technique demonstrates how recursion can simplify problems.",
    kind: "info screen",
    title: "Sum of Digits",
  },
  {
    code: "",
    content:
      "#### When to Use Recursion\n\n- **Divide and Conquer**: When you can break down the problem into smaller sub-problems.\n- **Tree-like Structures**: Useful for traversing trees or graphs.\n- **Simplification**: When recursion makes the code easier to read and understand.\n\nBeing strategic about when to use recursion will help you write more efficient code.",
    kind: "info screen",
    title: "When to Use Recursion",
  },
  {
    code: "",
    content: "What is the base case in a recursive function?",
    options: [
      "The condition to proceed with recursion",
      "The condition to stop the recursion",
      "The main function where recursion starts",
    ],
    solution: 1,
    kind: "multiple choice",
    title: "Understanding Base Case",
  },
  {
    code:
      '```go\npackage main\nimport "fmt"\nfunc printReverse(str string) {\n  if len(str) == 0 {\n    return\n  }\n  fmt.Print(string(str[len(str)-1]))\n  printReverse(str[:len(str)-1])\n}\nfunc main() {\n  printReverse("GoLang")\n}\n```',
    content:
      "## Reverse a String\n\n#### Manipulating Strings\nRecursion is also effective for string manipulation. Here, we reverse a string by printing its last character first and then calling the function on the remaining string.\n\n- **Base Case**: ( \text{len(str) == 0} )\n- **Recursive Case**: ( \text{print last character and call on remaining string} )\n\nThis showcases the flexibility of recursion.",
    kind: "info screen",
    title: "Reverse a String",
  },
  {
    code: "",
    content:
      "#### Pitfalls to Avoid\n\n- **Infinite Recursion**: Make sure your base case is reachable.\n- **Stack Overflow**: Excessive recursive calls can exhaust the stack memory.\n- **Duplicate Computations**: Be cautious with problems where you end up solving the same sub-problem multiple times.\n\nBeing aware of these pitfalls can help you write better recursive functions.",
    kind: "info screen",
    title: "Pitfalls of Recursion",
  },
  {
    code:
      '```go\npackage main\nimport "fmt"\nfunc power(base, exponent int) int {\n  if exponent == 0 {\n    return 1\n  }\n  return base * power(base, exponent-1)\n}\nfunc main() {\n  fmt.Println(power(2, 3))\n}\n```',
    content:
      "## Exponentiation\n\n#### Raising Numbers to Powers\nYou can calculate the result of raising a number to a power using recursion. For instance, (2^3 = 2 \times 2 \times 2 = 8).\n\n- **Base Case**: ( \text{exponent == 0} )\n- **Recursive Case**: ( \text{base} \times \text{power(base, exponent-1)} )\n\nThis example reveals how recursion can be used for mathematical calculations.",
    kind: "info screen",
    title: "Exponentiation",
  },
  {
    code: "",
    content:
      "- **Basic Examples**: Factorial and Fibonacci sequences are the go-to examples for understanding recursion.\n- **Advanced Usage**: From string manipulation to tree traversal, recursion has various applications.\n- **When and When Not to Use**: Know the scenarios where recursion is beneficial and where it's not.\n- **Pitfalls**: Watch out for infinite loops, stack overflows, and duplicate computations.\n\nBy mastering recursion in Go, you'll have a powerful tool at your disposal.",
    kind: "info screen",
    title: "Summary",
  },
];

export const math = [
  {
    code:
      "```javascript\nlet matrixA = [[1, 2], [3, 4]];\nconsole.log('Matrix A:', matrixA);\n```",
    content:
      "## Introduction to Matrices\n\n#### What is a Matrix?\nA matrix is a two-dimensional array used for storing data or simplifying complex operations. Matrices are fundamental in machine learning.\n\n**Try It**: Modify `matrixA` to be a 3x3 matrix and observe the output.",
    kind: "info screen",
    title: "What is a Matrix?",
  },
  {
    code:
      "```javascript\nlet matrixA = [[1, 2], [3, 4]];\nlet transposed = matrixA[0].map((col, i) => matrixA.map(row => row[i]));\nconsole.log('Transposed:', transposed);\n```",
    content:
      "#### Transposing a Matrix\nTransposition flips a matrix over its diagonal. It's crucial in many machine learning algorithms.\n\n**Try It**: Change the values in `matrixA` and see how the transposed matrix changes.",
    kind: "info screen",
    title: "Transposing a Matrix",
  },
  {
    code:
      "```javascript\nlet matrixA = [[1, 2], [3, 4]];\nlet matrixB = [[5, 6], [7, 8]];\nlet sum = matrixA.map((row, i) => row.map((val, j) => val + matrixB[i][j]));\nconsole.log('Sum:', sum);\n```",
    content:
      "#### Matrix Addition\nAdding two matrices involves adding their corresponding elements.\n\n**Try It**: Modify `matrixA` and `matrixB` to different sizes. What happens?",
    kind: "info screen",
    title: "Matrix Addition",
  },
  {
    code: "",
    content: "What operation adds corresponding elements of two matrices?",
    options: [
      "Matrix Multiplication",
      "Matrix Division",
      "Matrix Addition",
      "Matrix Subtraction",
    ],
    solution: 2,
    kind: "multiple choice",
    title: "Quick Quiz on Matrix Addition",
  },
  {
    code:
      "```javascript\nlet matrixA = [[1, 2], [3, 4]];\nlet matrixB = [[5, 6], [7, 8]];\nlet product = matrixA.map((row, i) => matrixB[0].map((_, j) => row.reduce((acc, _, k) => acc + matrixA[i][k] * matrixB[k][j], 0)));\nconsole.log('Product:', product);\n```",
    content:
      "#### Matrix Multiplication\nIn matrix multiplication, each element is calculated by taking the dot product of the corresponding row and column from the matrices being multiplied.\n\n**Try It**: Modify the matrices and observe how the product changes.",
    kind: "info screen",
    title: "Matrix Multiplication",
  },
  {
    code: "",
    content:
      "Matrix multiplication involves taking the dot product of rows and columns.",
    solution: true,
    kind: "swipe",
    title: "True or False: Matrix Multiplication",
  },
  {
    code: "",
    content:
      "- **Introduction to Matrices**: Basic structure and usage in ML.\n- **Transposing a Matrix**: How and why to transpose.\n- **Matrix Addition**: Adding corresponding elements.\n- **Matrix Multiplication**: Understanding dot products.\n- **Best Practices**: Always verify matrix dimensions before operations.",
    kind: "info screen",
    title: "Summary of Linear Algebra in JS",
  },
];

export const fourier = [
  {
    code:
      "```cpp\n#include <iostream>\n#include <vector>\n\nint main() {\n    std::vector<int> nums = {1, 2, 3};\n    std::cout << nums[1] << std::endl;\n    return 0;\n}\n```",
    content:
      "## Introduction\n\n#### What is an STL Vector?\nAn STL (Standard Template Library) vector in C++ is a dynamic array. Unlike arrays, vectors can be resized.\n\n**Dynamic Nature**: Vectors can grow or shrink in size.\n\n```cpp\nstd::vector<int> nums = {1, 2, 3};\n```\n\nLet's familiarize ourselves with the basics of vectors by understanding their declaration and accessing elements. Try changing the accessed index of the vector:",
    kind: "info screen",
    title: "STL Vector Basics",
  },
  {
    code:
      "```cpp\n#include <iostream>\n#include <vector>\n\nint main() {\n    std::vector<int> nums = {1, 2, 3};\n    for (int num : nums) {\n        std::cout << num << ' ';\n    }\n    return 0;\n}\n```",
    content:
      "#### Iterators\nIterators provide a way to access elements of a container, like the STL vector. They are similar to pointers.\n\n```cpp\nfor (int num : nums) { ... }\n```\n\nIterators are an essential concept in C++ and are extensively used with STL containers. Modify the loop to print squared values of the numbers:",
    kind: "info screen",
    title: "Using Iterators with Vector",
  },
  {
    code: "",
    content:
      "#### Best Practices\n\n- **Preallocate Memory**: If you know the size beforehand, use `reserve()` to avoid frequent reallocations.\n\n- **Avoid Unnecessary Copies**: Use references or pointers with iterators.\n\nBy adhering to these practices, you'll write efficient C++ code.",
    kind: "info screen",
    title: "Vector Best Practices",
  },
  {
    code: "",
    content: "Which of the following is not a valid method for STL vector?",
    options: ["push_back()", "pop()", "reserve()", "at()"],
    solution: 1,
    kind: "multiple choice",
    explanation:
      "`pop()` is not a valid method for STL vector. The correct method to remove the last element is `pop_back()`.",
    title: "Vector Methods Quiz",
  },
  {
    code:
      "```cpp\n#include <iostream>\n#include <vector>\n\nint main() {\n    std::vector<int> nums = {1, 2, 3, 4, 5};\n    nums.erase(nums.begin() + 2);\n    for (int num : nums) {\n        std::cout << num << ' ';\n    }\n    return 0;\n}\n```",
    content:
      "#### Modifying Vectors\n\nVectors in C++ provide various methods to modify their content, such as `push_back()`, `pop_back()`, and `erase()`.\n\n```cpp\nnums.erase(nums.begin() + 2);\n```\n\nIn the example above, we remove the third element from the vector. Try removing a different element:",
    kind: "info screen",
    title: "Modifying Vectors",
  },
  {
    code: "",
    content:
      "Iterators get invalidated after erasing an element from the vector.",
    solution: true,
    kind: "swipe",
    explanation:
      "True, erasing elements from a vector can lead to iterator invalidation. It's crucial to be careful when modifying a vector while iterating.",
    title: "Iterator Invalidation Quiz",
  },
  {
    code: "",
    content:
      "- **STL Vector**: A dynamic array that can grow and shrink.\n- **Iterators**: Used to access and traverse elements of STL containers.\n- **Modification**: Vectors provide methods like `push_back()`, `pop_back()`, and `erase()` for manipulation.\n- **Best Practices**: Preallocate memory, avoid unnecessary copies, and be cautious of iterator invalidation.",
    kind: "info screen",
    title: "Summary",
  },
];
